import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { retry, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@app/_services';
import { OwnerProfile } from './owner-profile';
import { SharedService } from './../_shared/shared.service';

@Injectable({ providedIn: 'root' })
export class OwnerProfileService {

	listingStatuses = [
		{ name: 'Onboarding', value: 'onboarding' },
		{ name: 'Active', value: 'active' },
		{ name: 'Quit', value: 'quit' },
		{ name: 'Fired', value: 'fired' },
	];

	satisfactionRanks = [
		{ value: 1, name: '1 Very Unhappy' },
		{ value: 2, name: '2 Unhappy' },
		{ value: 3, name: '3 Somewhat Unhappy' },
		{ value: 4, name: '4 Slightly Unhappy' },
		{ value: 5, name: '5 Neutral' },
		{ value: 6, name: '6 Slightly Happy' },
		{ value: 7, name: '7 Somewhat Happy' },
		{ value: 8, name: '8 Happy' },
		{ value: 9, name: '9 Very Happy' },
		{ value: 10, name: '10 Extremely Happy' },
	];

	contactPrefs = [
		{ name: 'Email', value: 'email' },
		{ name: 'Mobile', value: 'mobile' },
		{ name: 'Email and Mobile', value: 'email and mobile' },
	];

	temperaments = [
		{ name: 'Uninvolved - Low Maintenance', value: 1 },
		{ name: 'Involved - Quarterly Meetings Required', value: 2 },
		{ name: 'Involved - Monthly Meetings Required', value: 3 },
		{ name: 'Overly Involved - Expectations Reset Required', value: 4 },
		{ name: 'Overly Involved - Termination Recommended', value: 5 },
	];

	incomeSensitivities = [
		{ name: 'Not concerned', value: 1 },
		{ name: 'Very low concern', value: 2 },
		{ name: 'Low concern', value: 3 },
		{ name: 'Slight concern', value: 4 },
		{ name: 'Moderate', value: 5 },
		{ name: 'Somewhat sensitive', value: 6 },
		{ name: 'Sensitive', value: 7 },
		{ name: 'Highly sensitive', value: 8 },
		{ name: 'Very sensitive', value: 9 },
		{ name: 'Extremely sensitive', value: 10 },
	];

	maintenanceSensitivities = [
		{ name: 'Not concerned', value: 1 },
		{ name: 'Very low concern', value: 2 },
		{ name: 'Low concern', value: 3 },
		{ name: 'Slight concern', value: 4 },
		{ name: 'Moderate', value: 5 },
		{ name: 'Somewhat sensitive', value: 6 },
		{ name: 'Sensitive', value: 7 },
		{ name: 'Highly sensitive', value: 8 },
		{ name: 'Very sensitive', value: 9 },
		{ name: 'Extremely sensitive', value: 10 },
	];

	engagementFreqOptions = [
		{ value: 1, name: 'Never' },
		{ value: 2, name: 'Minimal' },
		{ value: 3, name: 'Monthly' },
		{ value: 4, name: 'Frequently' },
		{ value: 5, name: 'Constant Time Suck' }
	];

	ownerQualityOptions = [
		{ value: 1, name: 'Hostile (Need to fire)' },
		{ value: 2, name: 'Difficult' },
		{ value: 3, name: 'Difficult but bearable' },
		{ value: 4, name: 'Okay' },
		{ value: 5, name: 'Neutral' },
		{ value: 6, name: 'Decent' },
		{ value: 7, name: 'Good' },
		{ value: 8, name: 'Pleasant' },
		{ value: 9, name: 'Great' },
		{ value: 10, name: 'Amazing Human Being' },
	];

	maintServiceFreqOptions = [
		{ value: 1, name: 'Infrequent' },
		{ value: 2, name: 'Very Rarely' },
		{ value: 3, name: 'Rarely' },
		{ value: 4, name: 'Occasionally' },
		{ value: 5, name: 'Sometimes' },
		{ value: 6, name: 'Moderately Often' },
		{ value: 7, name: 'Often' },
		{ value: 8, name: 'Very Often' },
		{ value: 9, name: 'Almost Always' },
		{ value: 10, name: 'All the time' },
	];

	titles = [
		{ name: 'Mr.', value: 'Mr.' },
		{ name: 'Mrs.', value: 'Mrs.' },
		{ name: 'Ms.', value: 'Ms.' },
		{ name: 'Dr.', value: 'Dr.' },
	];

	portalAccesses = [
		{ name: 'NO', value: false },
		{ name: 'YES', value: true },
	];

	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
			private sharedService: SharedService
		) { }

	urlEndpoint = 'owner-profiles';

	add(ownerProfile: OwnerProfile): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(ownerProfile));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, ownerProfile);
	}

	update(ownerProfile: OwnerProfile): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, ownerProfile).pipe();
	}

	updateOwnerPortalAccess(ownerProfile: OwnerProfile): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/owner-profiles-access-rights`, ownerProfile);
	}

	delete(developmentId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${developmentId}`);
	}

	findAll(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/find-all`).pipe(retry({ count: 2, delay: 1500 }));
	}

	getFilterData(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/filterData`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		console.log('posting... ' + JSON.stringify(filters));
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));

	}

	findOwnerProfileDropDown(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/owner-profile-drop-down`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	checkDuplicateEmailAddress(emailAddress: any): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/duplicate-emails/${emailAddress}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}

	uploadFileForOwnerProfile(file: any, ownerProfileId: any) {
	    const formData: FormData = new FormData();
	    formData.append('file', file, file.name);
		if (ownerProfileId) {
			formData.append('ownerProfileId', ownerProfileId);
		}
	     //append any other key here if required
	    return this.http.post(`${environment.apiUrl}/pictures/owner-profile/upload`, formData);
	  }


	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any) {
		try {
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta) {
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++) {
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters) {
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++) {
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error) {
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}

	importOwnerProfile(contactdata: any): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(contactdata));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/import`, contactdata);
	}
}
